"use client";

import { useRef, useState } from "react";
import type { ReactNode } from "react";
import { Apple, CalendarClock, Globe2, Link2 } from "lucide-react";
import GradientText from "@/components/GradientText";
import { REGISTER_URL } from "@/components/pricing/data";
import { flushSync } from "react-dom";
import { Button, CODE, SectionShell, TrafficDots, useBandRowAlign, useWholeRowBlocks } from "./kit";
import { gsap, NOT_REDUCED, useGSAP, useMaskedReveal } from "./kit/motion";

type RuleId = "ios" | "geo" | "expire" | "slug";

interface ForgeState {
  destination: string;
  rules: Record<RuleId, boolean>;
}

/** `premium`: routing rules are Premium-only (plans.py) — the chip says so. */
const RULES: { id: RuleId; label: string; Icon: typeof Apple; premium?: boolean }[] = [
  { id: "ios", label: "iOS route", Icon: Apple, premium: true },
  { id: "geo", label: "geo DE", Icon: Globe2, premium: true },
  { id: "expire", label: "expires", Icon: CalendarClock },
  { id: "slug", label: "custom slug", Icon: Link2 },
];

/** The idle-demo destination (UTM soup on purpose) — Task 2 types this. */
export const DEMO_DEST =
  "https://myshop.com/collections/summer?utm_source=instagram&utm_campaign=q3";

/** Static/reduced-motion/mobile default: the finished "good" state — a
 *  request that works on every plan (no Premium-only routing rule). */
const INITIAL: ForgeState = {
  destination: DEMO_DEST,
  rules: { ios: false, geo: false, expire: true, slug: true },
};

/** Constant sample values — no randomness in render paths. The shapes follow
 *  the real POST /v1/links contract: `url`, optional `slug`, `routing` as an
 *  ordered array of `{ if, to }` rules, `expires_at`; the 201 is the link. */
const CUSTOM_SLUG = "summer-sale";
const RANDOM_SLUG = "x7q2k9mb";
const LINK_ID = "lnk_8h2f0qZ3Tk1…";
const IOS_DEST = "apps.apple.com/id6448311069";
const GEO_DEST = "myshop.de/sommer";
const EXPIRES = "2026-12-31T23:00:00Z";
const CREATED = "2026-08-12T09:14:03Z";

/** Middle-truncate for JSON display so line count/width never grows. */
function shorten(url: string, max = 38): string {
  if (url.length <= max) return url;
  return `${url.slice(0, max - 12)}…${url.slice(-11)}`;
}

/** JSON panes show URLs scheme-less and middle-truncated — the input keeps
 *  the full value; only the rendered request/response lines are compacted. */
function displayUrl(url: string, max = 22): string {
  return shorten(url.replace(/^https?:\/\//, ""), max);
}

interface ForgeLine {
  key: string;
  jsx: ReactNode;
}

/** The POST body, derived from state. Comma placement handled by building
 *  the plain-text skeleton first, then colorizing. */
function requestLines(s: ForgeState): ForgeLine[] {
  const { ios, geo, expire, slug } = s.rules;
  const routing = ios || geo;
  const dest = s.destination || "…";
  const lines: ForgeLine[] = [];
  const K = (t: string) => <span className={CODE.key}>&quot;{t}&quot;</span>;
  const S = (t: string) => <span className={CODE.string}>&quot;{t}&quot;</span>;
  const tail = (more: boolean) => (more ? "," : "");

  lines.push({ key: "open", jsx: <>{"{"}</> });
  lines.push({
    key: "dest",
    jsx: (
      <>
        {"  "}
        {K("url")}: {S(displayUrl(dest))}
        {tail(slug || routing || expire)}
      </>
    ),
  });
  if (slug)
    lines.push({
      key: "slug",
      jsx: (
        <>
          {"  "}
          {K("slug")}: {S(CUSTOM_SLUG)}
          {tail(routing || expire)}
        </>
      ),
    });
  if (routing) {
    lines.push({ key: "routing-open", jsx: <>{"  "}{K("routing")}: {"["}</> });
    if (ios) {
      lines.push({
        key: "ios",
        jsx: (
          <>
            {"    { "}
            {K("if")}: {"{ "}
            {K("device")}: {S("ios")}
            {" },"}
          </>
        ),
      });
      lines.push({
        key: "ios-to",
        jsx: (
          <>
            {"      "}
            {K("to")}: {S(displayUrl(IOS_DEST, 20))}
            {" }"}
            {tail(geo)}
          </>
        ),
      });
    }
    if (geo) {
      lines.push({
        key: "geo",
        jsx: (
          <>
            {"    { "}
            {K("if")}: {"{ "}
            {K("country")}: {S("DE")}
            {" },"}
          </>
        ),
      });
      lines.push({
        key: "geo-to",
        jsx: (
          <>
            {"      "}
            {K("to")}: {S(GEO_DEST)}
            {" }"}
          </>
        ),
      });
    }
    lines.push({ key: "routing-close", jsx: <>{"  ]"}{tail(expire)}</> });
  }
  if (expire)
    lines.push({
      key: "expire",
      jsx: (
        <>
          {"  "}
          {K("expires_at")}: {S(EXPIRES)}
        </>
      ),
    });
  lines.push({ key: "close", jsx: <>{"}"}</> });
  return lines;
}

/** The 201 body (the link object), derived from the last *applied* state.
 *  The routing array is echoed in full by the API; the pane abbreviates it. */
function responseLines(s: ForgeState): ForgeLine[] {
  const { ios, geo, expire, slug } = s.rules;
  const ruleCount = (ios ? 1 : 0) + (geo ? 1 : 0);
  const path = slug ? CUSTOM_SLUG : RANDOM_SLUG;
  const lines: ForgeLine[] = [];
  const K = (t: string) => <span className={CODE.key}>&quot;{t}&quot;</span>;
  const S = (t: string) => <span className={CODE.string}>&quot;{t}&quot;</span>;
  const NULL = <span className={CODE.bool}>null</span>;

  lines.push({ key: "open", jsx: <>{"{"}</> });
  lines.push({ key: "id", jsx: <>{"  "}{K("id")}: {S(LINK_ID)},</> });
  lines.push({ key: "slug", jsx: <>{"  "}{K("slug")}: {S(path)},</> });
  lines.push({ key: "domain", jsx: <>{"  "}{K("domain")}: {S("rii.link")},</> });
  lines.push({ key: "short", jsx: <>{"  "}{K("short_url")}: {S(`https://rii.link/${path}`)},</> });
  lines.push({
    key: "dest",
    jsx: <>{"  "}{K("url")}: {S(displayUrl(s.destination || "…"))},</>,
  });
  lines.push({
    key: "routing",
    jsx: (
      <>
        {"  "}
        {K("routing")}: {ruleCount === 0 ? "[]" : ruleCount === 1 ? "[{ … }]" : "[{ … }, { … }]"},
      </>
    ),
  });
  lines.push({
    key: "expire",
    jsx: <>{"  "}{K("expires_at")}: {expire ? S(EXPIRES) : NULL},</>,
  });
  lines.push({ key: "created", jsx: <>{"  "}{K("created_at")}: {S(CREATED)},</> });
  lines.push({ key: "updated", jsx: <>{"  "}{K("updated_at")}: {S(CREATED)}</> });
  lines.push({ key: "close", jsx: <>{"}"}</> });
  return lines;
}

const SR_SUMMARY =
  "Interactive example: you type a destination URL and toggle options — iOS route, geo DE, " +
  "expiry, custom slug. A POST request to /v1/links writes itself with the url, an optional slug, " +
  "a routing array of if/to rules and expires_at matching your choices, and a 201 Created response " +
  "answers with the link object: id, slug, the short_url (for example https://rii.link/summer-sale), " +
  "the routing array and timestamps. The panes are a simulation of the real API contract; routing " +
  "rules need a Premium plan.";

/**
 * DevelopersForgeHero — the /developers signature: a light grid-paper hero
 * whose right column is a real playground. The visitor types a destination
 * and flips rule chips; the POST body rewrites live and the 201 response
 * answers after a beat. Static/mobile/reduced-motion default is the finished
 * demo state (INITIAL). Task 2 adds the idle autoplay on top.
 *
 * Layout mirrors AgentHeroSection: ONE [data-grid-block] wrapping both
 * columns; nav row 1 + one free row = content starts row 3 (light hero, so
 * 2*cell top padding); useWholeRowBlocks/useBandRowAlign do the row math.
 */
export default function DevelopersForgeHero() {
  // `draft` renders the request pane + controls; `applied` renders the
  // response pane, following after a short beat (the "server answers").
  const [draft, setDraft] = useState<ForgeState>(INITIAL);
  const [applied, setApplied] = useState<ForgeState>(INITIAL);
  // Task 2 flips this in the matchMedia scope; flashes/delays only then.
  const [enhanced, setEnhanced] = useState(false);
  const enhancedRef = useRef(false);

  const blockRef = useRef<HTMLDivElement>(null);
  const copyRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  // First real user interaction kills the Task-2 autoplay permanently.
  const interactedRef = useRef(false);
  const killAutoplayRef = useRef<(() => void) | null>(null);
  const commitTimerRef = useRef<number | undefined>(undefined);
  // Latest draft, readable from inside tl.calls without stale closures.
  const readDraftRef = useRef<ForgeState>(INITIAL);
  readDraftRef.current = draft;

  /** Flash the given response/request lines (imperative tween — feedback,
   *  not choreography). Safe to call any time; no-op when not enhanced. */
  const flashLines = (keys: string[], pane: "rq" | "rs") => {
    if (!enhancedRef.current || !panelRef.current) return;
    keys.forEach((k) => {
      const el = panelRef.current!.querySelector(`[data-${pane}-line="${k}"]`);
      if (!el) return;
      gsap.fromTo(
        el,
        { backgroundColor: "rgba(250,67,127,0.20)" },
        { backgroundColor: "rgba(250,67,127,0)", duration: 0.7, ease: "power2.out", clearProps: "backgroundColor" },
      );
    });
  };

  /** Apply a draft change; the response follows after a beat (enhanced) or
   *  instantly (static path). `changed` = semantic line keys to flash. */
  const applyChange = (next: ForgeState, changed: string[]) => {
    readDraftRef.current = next;
    setDraft(next);
    if (changed.length) requestAnimationFrame(() => flashLines(changed, "rq"));
    window.clearTimeout(commitTimerRef.current);
    if (enhancedRef.current) {
      commitTimerRef.current = window.setTimeout(() => {
        // Commit the LATEST draft, not the captured snapshot — whatever
        // interleaving happened, the response always converges on the
        // request. Real-clock timeout on purpose: gsap.delayedCall rides
        // rAF, which occluded windows starve (see insure() in kit/motion).
        setApplied(readDraftRef.current);
        requestAnimationFrame(() => {
          flashLines(changed, "rs");
          const status = panelRef.current?.querySelector("[data-rs-status]");
          if (status)
            gsap.fromTo(
              status,
              { scale: 1.12 },
              { scale: 1, duration: 0.4, ease: "power3.out", clearProps: "scale" },
            );
        });
      }, 450);
    } else {
      setApplied(next);
    }
  };

  /** Kills the Task-2 autoplay on the first genuine interaction. */
  const takeover = () => {
    if (interactedRef.current) return;
    interactedRef.current = true;
    killAutoplayRef.current?.();
  };

  // Idle autoplay — a finite demo pass (empty → typed URL → +iOS → +custom slug),
  // completion-chained restart (NEVER repeat:-1), IO-gated, killed forever
  // by the first real interaction. All state writes ride tl.call (flushSync
  // + imperative feedback tweens — the sanctioned idiom); the typing beat
  // drives plain async setState from a tween's onUpdate, which is fine (the
  // DOM doesn't need to be synchronous mid-typing).
  const holdRef = useRef<number>(0);
  const visibleRef = useRef(false);

  useGSAP(
    () => {
      const mm = gsap.matchMedia();
      mm.add(`${NOT_REDUCED} and (min-width: 768px)`, () => {
        setEnhanced(true);
        enhancedRef.current = true;
        if (interactedRef.current)
          return () => {
            window.clearTimeout(commitTimerRef.current);
            setApplied(readDraftRef.current);
            enhancedRef.current = false;
            setEnhanced(false);
          }; // user beat us to it (StrictMode re-run)

        const EMPTY: ForgeState = {
          destination: "",
          rules: { ios: false, geo: false, expire: false, slug: false },
        };
        const typing = { n: 0 };
        const setBoth = (s: ForgeState) => {
          setDraft(s);
          setApplied(s);
        };
        const pressChip = (id: RuleId) => {
          const chip = panelRef.current?.closest("section")?.querySelector(`[data-forge-chip="${id}"]`);
          if (chip)
            gsap.fromTo(chip, { scale: 0.92 }, { scale: 1, duration: 0.35, ease: "back.out(3)", clearProps: "scale" });
        };
        const demoToggle = (id: RuleId, changed: string[]) => () => {
          flushSync(() =>
            setDraft((d) => ({ ...d, rules: { ...d.rules, [id]: true } })),
          );
          pressChip(id);
          flashLines(changed, "rq");
        };
        // Response mirrors the CURRENT draft (set by earlier calls in this
        // same timeline) — read through readDraftRef (Step 3) to avoid stale
        // closures.
        const demoAnswer = (changed: string[]) => () => {
          flushSync(() => setApplied(readDraftRef.current));
          flashLines(changed, "rs");
          const status = panelRef.current?.querySelector("[data-rs-status]");
          if (status)
            gsap.fromTo(status, { scale: 1.12 }, { scale: 1, duration: 0.4, ease: "power3.out", clearProps: "scale" });
        };

        const tl = gsap.timeline({
          paused: true,
          defaults: { ease: "power3.out", immediateRender: false },
        });
        tl.call(() => {
          typing.n = 0;
          flushSync(() => setBoth(EMPTY));
        })
          // Type the demo URL into the real input (state-driven).
          .to(typing, {
            n: DEMO_DEST.length,
            duration: 2.6,
            ease: "none",
            onUpdate: () =>
              setDraft((d) => ({ ...d, destination: DEMO_DEST.slice(0, Math.round(typing.n)) })),
          }, "+=0.6")
          // Server answers the plain create.
          .call(demoAnswer(["short", "slug", "dest"]), undefined, "+=0.5")
          // + iOS route.
          .call(demoToggle("ios", ["ios", "ios-to", "routing-open", "routing-close"]), undefined, "+=1.4")
          .call(demoAnswer(["routing"]), undefined, "+=0.5")
          // + custom slug.
          .call(demoToggle("slug", ["slug"]), undefined, "+=1.4")
          .call(demoAnswer(["slug", "short"]), undefined, "+=0.5")
          // Hold on the finished state so it can be read.
          .to({}, { duration: 4 });

        // The demoAnswer beats need the CURRENT draft (set by earlier calls
        // in the same timeline) — mirror it through a ref kept fresh below.
        // (readDraftRef is written by the render body; see Step 3.)

        const REPLAY_HOLD_MS = 60;
        const scheduleReplay = () => {
          window.clearTimeout(holdRef.current);
          holdRef.current = window.setTimeout(() => {
            if (visibleRef.current && !interactedRef.current) tl.restart();
          }, REPLAY_HOLD_MS);
        };
        tl.eventCallback("onComplete", scheduleReplay);

        const io = new IntersectionObserver(
          (entries) =>
            entries.forEach((e) => {
              visibleRef.current = e.isIntersecting;
              if (interactedRef.current) return;
              if (!e.isIntersecting) {
                tl.pause();
                return;
              }
              if (tl.progress() === 1) tl.restart();
              else tl.play();
            }),
          { threshold: 0.3 },
        );
        if (panelRef.current) io.observe(panelRef.current);

        // First real interaction: stop the demo wherever it is, keep the
        // current state (controls are already live), never restart.
        killAutoplayRef.current = () => {
          window.clearTimeout(holdRef.current);
          tl.kill();
        };

        return () => {
          io.disconnect();
          window.clearTimeout(holdRef.current);
          window.clearTimeout(commitTimerRef.current);
          setApplied(readDraftRef.current);
          tl.kill();
          killAutoplayRef.current = null;
          enhancedRef.current = false;
          setEnhanced(false);
        };
      });
    },
    { scope: panelRef },
  );

  const onDestinationChange = (value: string) => {
    takeover();
    // Typing gets no flash — the text itself is the feedback.
    applyChange({ ...readDraftRef.current, destination: value }, []);
  };

  const toggleRule = (id: RuleId) => {
    takeover();
    const cur = readDraftRef.current;
    const next: ForgeState = {
      ...cur,
      rules: { ...cur.rules, [id]: !cur.rules[id] },
    };
    // Flash the whole routing block when its shape changes.
    const changed =
      id === "ios" || id === "geo"
        ? [id, `${id}-to`, "routing", "routing-open", "routing-close"]
        : id === "slug"
          ? ["slug", "short"]
          : [id];
    applyChange(next, changed);
  };

  useWholeRowBlocks(blockRef);
  useBandRowAlign(blockRef);
  useMaskedReveal(copyRef, {
    heading: "h1",
    sub: "[data-hero-sub]",
    prepare: (root) => {
      gsap.set(root.querySelector("[data-hero-halo]"), { autoAlpha: 0 });
    },
    onDone: (root) => {
      gsap.to(root.querySelector("[data-hero-halo]"), { autoAlpha: 1, duration: 0.8, ease: "power2.out" });
    },
  });

  const rq = requestLines(draft);
  const rs = responseLines(applied);

  return (
    <SectionShell id="ref-forge-hero" decoAvoid className="md:pt-px md:pb-px">
      <div className="pt-24 pb-4 md:pb-0 md:pt-[calc(2*var(--grid-cell,72px))]">
        <div
          ref={blockRef}
          data-grid-block
          className="grid items-start gap-10 md:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]"
        >
          {/* Left — hero copy. */}
          <div ref={copyRef} className="min-w-0">
            <h1 className="text-5xl font-extrabold leading-[1.06] tracking-tight text-slate-900 md:mt-[10px] md:text-6xl">
              Build on the{" "}
              <span className="relative inline-block whitespace-nowrap">
                <span
                  data-hero-halo
                  aria-hidden="true"
                  className="pointer-events-none absolute left-1/2 top-1/2 -z-10 h-[200%] w-[150%] -translate-x-1/2 -translate-y-1/2 animate-cube-pulse rounded-full blur-[70px] [background:radial-gradient(ellipse,rgba(250,67,127,0.4),transparent_70%)]"
                />
                <GradientText colors={["#800938", "#d70e62", "#fa437f"]} animationSpeed={2} yoyo={false}>
                  link layer.
                </GradientText>
              </span>
            </h1>

            <p data-hero-sub className="mt-5 max-w-[480px] text-[clamp(16px,1.4vw,19px)] leading-[1.6] text-muted">
              One REST API for links, smart routing and analytics — try the
              request shape right here. Type a destination, flip a rule, watch
              the request write itself.
            </p>

            <div data-hero-sub className="mt-8 flex flex-wrap gap-3">
              <Button href={REGISTER_URL}>Create a free account</Button>
              <Button href="#ref-api" variant="ghost">
                Browse the endpoints
              </Button>
            </div>
          </div>

          {/* Right — the Link Forge playground. */}
          <div className="min-w-0">
            <p className="sr-only">{SR_SUMMARY}</p>

            <div className="overflow-hidden rounded-ref-lg border border-line-2 bg-[#0d0d12] shadow-ref-lg">
              {/* Chrome bar. */}
              <div className="flex items-center gap-2 border-b border-white/[0.07] bg-[#16161d] px-4 py-[11px]">
                <TrafficDots />
                <span className="ml-1.5 font-jetbrains text-[12.5px] text-[#73738a]">link&nbsp;forge</span>
                <span className="ml-auto rounded-[7px] border border-white/10 px-[9px] py-1 font-jetbrains text-[11px] text-white/60">
                  POST /v1/links
                </span>
              </div>

              {/* Controls — REAL, labeled, always live. */}
              <div className="border-b border-white/[0.07] p-4">
                <label
                  htmlFor="forge-destination"
                  className="mb-1.5 block font-jetbrains text-[11px] uppercase tracking-[0.08em] text-white/45"
                >
                  destination
                </label>
                <input
                  id="forge-destination"
                  type="url"
                  spellCheck={false}
                  autoComplete="off"
                  value={draft.destination}
                  onFocus={takeover}
                  onChange={(e) => onDestinationChange(e.target.value)}
                  data-forge-input
                  className="w-full rounded-[9px] border border-white/10 bg-white/[0.04] px-3 py-2 font-jetbrains text-[13px] text-[#e6e6f0] outline-none transition-colors placeholder:text-white/30 focus:border-brand-400/60"
                  placeholder="https://your-long-campaign-url.com/…"
                />
                <div role="group" aria-label="Routing rules" className="mt-2.5 flex flex-wrap gap-1.5">
                  {RULES.map(({ id, label, Icon, premium }) => (
                    <button
                      key={id}
                      type="button"
                      aria-pressed={draft.rules[id]}
                      onClick={() => toggleRule(id)}
                      data-forge-chip={id}
                      className={`inline-flex items-center gap-1.5 rounded-lg border px-2.5 py-1 font-jetbrains text-xs transition-colors ${
                        draft.rules[id]
                          ? "border-brand-400/60 bg-brand-600/20 text-white"
                          : "border-white/10 bg-white/[0.04] text-white/55 hover:text-white/80"
                      }`}
                    >
                      <Icon size={12} aria-hidden="true" />
                      <span aria-hidden="true">{draft.rules[id] ? "−" : "+"}</span> {label}
                      {premium && (
                        <span className="rounded-[4px] bg-data-amber/20 px-1 text-[9px] tracking-[0.06em] text-data-amber">
                          PREMIUM
                        </span>
                      )}
                    </button>
                  ))}
                </div>
              </div>

              {/* Panes — simulation, hidden from AT (sr summary above). */}
              <div ref={panelRef} aria-hidden="true" className="grid gap-px bg-white/[0.07] lg:grid-cols-2">
                <div className="bg-[#0d0d12] p-4">
                  <div className="mb-2 flex items-center gap-2 font-jetbrains text-[11px]">
                    <span className="font-semibold text-white/80">request</span>
                    <span className="text-white/40">application/json</span>
                  </div>
                  <pre
                    className="m-0 whitespace-pre font-jetbrains text-[11.5px] leading-[1.75] text-[#d6d6e2]"
                    style={{ minHeight: 222 }}
                  >
                    {rq.map((l) => (
                      <span key={l.key} data-rq-line={l.key} className="block rounded-[4px]">
                        {l.jsx}
                      </span>
                    ))}
                  </pre>
                </div>
                <div className="bg-[#0d0d12] p-4">
                  <div className="mb-2 flex items-center gap-2 font-jetbrains text-[11px]">
                    <span className="font-semibold text-white/80">response</span>
                    <span
                      data-rs-status
                      className="inline-block rounded-[6px] bg-data-green/[0.15] px-1.5 py-0.5 font-semibold text-data-green"
                    >
                      201 Created
                    </span>
                  </div>
                  <pre
                    data-forge-response
                    className="m-0 whitespace-pre font-jetbrains text-[11.5px] leading-[1.75] text-[#d6d6e2]"
                    style={{ minHeight: 222 }}
                  >
                    {rs.map((l) => (
                      <span key={l.key} data-rs-line={l.key} className="block rounded-[4px]">
                        {l.jsx}
                      </span>
                    ))}
                  </pre>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </SectionShell>
  );
}
